import type {
  BatchArgs,
  BatchResponse,
  EditorSourceType,
  IAIPromptGroup,
  ICustomTemplateChart,
  IEditorDraft,
  IUser,
  Modification,
  SchemaType,
  SchemaTypeToContent,
  Selector,
} from '@carboncopyco/types';
import { Debug, removeUndefinedValues } from '@carboncopyco/utils';
import { v4 as uuid } from 'uuid';
import { handleBatchRequest } from '../utils';
import { mainApi } from './mainApi';

Debug.extend('demostack-data:api:editorApi');

export interface BaseArgs {
  organizationId: string;
}

export interface SaveDraftArgs extends BaseArgs {
  draftId: string;
  comment: string;
}

export interface DeleteDraftArgs extends BaseArgs {
  draftId: string;
}

export interface GetDraftArgs extends BaseArgs {
  draftId: string;
}

export interface GetDraftsArgs extends BaseArgs {
  sourceType?: EditorSourceType;
  sourceId: number;
}

export interface CreateChartTemplateArgs extends BaseArgs {
  payload: ICustomTemplateChart;
}

export type GetAllChartTemplatesArgs = BaseArgs;

export interface DeleteChartTemplateArgs extends BaseArgs {
  chartId: string;
}

export interface UpdateChartTemplateArgs extends BaseArgs {
  chartId: string;
  payload: ICustomTemplateChart;
}

export interface GetImageArgs {
  src: string;
  isDSImage: boolean;
}

export interface RefreshSessionArgs extends BaseArgs {
  recorderUuid: string;
  sessionId: string;
}

type EntityType = 'draft' | 'demo' | 'tour' | 'presentation';

export interface EntityDetails {
  entityType: EntityType;
  entityId: string;
  revisionId?: string;
}

interface ModificationDetails {
  modificationId: Modification['id'];
}

export interface GetModificationsArgs
  extends BaseArgs,
    EntityDetails,
    Pick<BatchArgs, 'limit'>,
    Partial<Pick<Modification, 'schemaType' | 'modificationType'>> {
  apiVersion?: number;
}

export type GetModificationArgs = BaseArgs &
  EntityDetails &
  ModificationDetails;

export interface CreateModificationArgs<T extends SchemaType = any>
  extends BaseArgs,
    Pick<EntityDetails, 'entityId'>,
    Partial<Pick<Modification, 'isLocked' | 'isActive'>> {
  entityType: Extract<EntityType, 'draft'>;
  schemaType: T;
  selectors?: Selector[];
  content: SchemaTypeToContent<T>;
  groupId?: string;
}

export interface UpdateModificationArgs<T extends SchemaType = any>
  extends BaseArgs,
    EntityDetails,
    ModificationDetails,
    Partial<Pick<Modification, 'isActive' | 'isLocked' | 'order'>> {
  selectors?: Selector[];
  content: SchemaTypeToContent<T>;
}

export type DeleteModificationArgs = BaseArgs &
  EntityDetails &
  ModificationDetails;

export type ReorderModificationsArgs = BaseArgs &
  EntityDetails & {
    id: Modification['id'];
    afterId: Modification['id'] | null;
  };

export type GetModificationsCountArgs = BaseArgs & {
  entityType?: EntityType;
  entityId?: number;
};

export type ModificationCountBySchemaType = {
  modificationType: Modification['modificationType'];
  schemaType: SchemaType;
  total: number;
  counts?: ModificationCountBySchemaType[];
};

export type GetModificationsCountResponse = {
  counts: ModificationCountBySchemaType[];
};

export type ImportModificationsArgs = BaseArgs & {
  draftId: string;
  entityType?: EntityType;
  entityId: number;
  schemaType?: SchemaType[];
};

export type ImportModificationsResponse = {
  success: true;
};

export type CreateSpreadsheetResponse = {
  id: string;
  spreadsheetUrl: string;
};
export type CreateSpreadsheetArgs = BaseArgs & {
  title?: string;
  values: string[][];
  draftId: string;
};
export type GetSpreadsheetValuesResponse = {
  values: string[][];
};
export type GetSpreadsheetValuesArgs = BaseArgs & {
  spreadsheetId: string;
};
export type EditWithAIResponse = {
  message: string;
  modified: unknown[];
  code: string;
  rawResponse: string;
  success: boolean;
};
export type EditWithAIArgs = {
  columnData: unknown[];
  columnName: string;
  prompt: string;
  userData?: IUser;
};

const editorApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    saveDraft: builder.mutation<IEditorDraft, SaveDraftArgs>({
      query: ({ organizationId, draftId, comment }) => ({
        url: `/api/${organizationId}/draft/save/${draftId}`,
        method: 'POST',
        body: { comment },
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'Revisions', id: result.sourceId },
              { type: 'Demo', id: result.sourceId },
              { type: 'Demos' },
              { type: 'Drafts', id: result.sourceId },
            ]
          : [{ type: 'Demos' }],
      onQueryStarted: async (
        { organizationId, draftId },
        { dispatch, queryFulfilled }
      ) => {
        const { data: draft } = await queryFulfilled;

        await dispatch(
          editorApi.util.upsertQueryData(
            'getDraft',
            {
              organizationId,
              draftId,
            },
            draft
          )
        );
      },
    }),

    deleteDraft: builder.mutation<IEditorDraft, DeleteDraftArgs>({
      query: ({ organizationId, draftId }) => ({
        url: `/api/${organizationId}/draft/${draftId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, _, args) =>
        result
          ? [
              { type: 'Draft', id: args.draftId },
              { type: 'Drafts', id: result.sourceId },
            ]
          : [{ type: 'Draft', id: args.draftId }],
    }),

    getDraft: builder.query<IEditorDraft, GetDraftArgs>({
      query: ({ organizationId, draftId }) => ({
        url: `/api/${organizationId}/draft/${draftId}`,
        method: 'GET',
      }),
      providesTags: (_, __, args) => [{ type: 'Draft', id: args.draftId }],
    }),

    getDrafts: builder.query<IEditorDraft[], GetDraftsArgs>({
      query: ({ organizationId, sourceType = 'demo', sourceId }) => ({
        url: `/api/${organizationId}/${sourceType}/${sourceId}/drafts`,
        method: 'GET',
      }),
      providesTags: (_, __, args) => [{ type: 'Drafts', id: args.sourceId }],
      transformResponse: (response: IEditorDraft[]) => {
        return response.map((draft) => ({
          ...draft,
          rules: draft.rules?.map((rule) =>
            rule.ruleId ? rule : { ...rule, ruleId: uuid() }
          ),
        }));
      },
    }),

    recorderSessionRefresh: builder.mutation<void, RefreshSessionArgs>({
      query: ({ organizationId, recorderUuid, sessionId }) => ({
        url: `/api/${organizationId}/recorder-session/refresh`,
        method: 'POST',
        body: { sessionId, recorderUuid },
      }),
    }),

    // Charts Templates
    createChartTemplate: builder.mutation<
      ICustomTemplateChart,
      CreateChartTemplateArgs
    >({
      query: ({ organizationId, payload }) => ({
        url: `/api/${organizationId}/chart/template/new`,
        method: 'PUT',
        body: { ...payload },
      }),
      invalidatesTags: ['ChartsTemplates'],
    }),

    getAllChartTemplates: builder.query<
      ICustomTemplateChart[],
      GetAllChartTemplatesArgs
    >({
      query: ({ organizationId }) => ({
        url: `/api/${organizationId}/chart/templates?limit=100`,
        method: 'GET',
      }),
      transformResponse: (response: BatchResponse<ICustomTemplateChart>) => {
        return response.items;
      },
      providesTags: ['ChartsTemplates'],
    }),

    deleteChartTemplate: builder.mutation<any, DeleteChartTemplateArgs>({
      query: ({ organizationId, chartId }) => ({
        url: `/api/${organizationId}/chart/template/${chartId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ChartsTemplates'],
    }),

    updateChartTemplate: builder.mutation<any, UpdateChartTemplateArgs>({
      query: ({ organizationId, chartId, payload }) => ({
        url: `/api/${organizationId}/chart/template/${chartId}`,
        method: 'POST',
        body: { ...payload },
      }),
      invalidatesTags: ['ChartsTemplates'],
    }),
    // End of Charts Templates

    getModifications: builder.query<Modification[], GetModificationsArgs>({
      queryFn: async (
        {
          organizationId,
          entityType,
          entityId,
          modificationType,
          schemaType,
          limit = 1000,
          apiVersion = 1,
          revisionId,
        },
        __,
        ___,
        baseQuery
      ) => {
        const fetchModifications = async (
          prevModifications: Modification[] = [],
          nextBatchCursor?
        ) => {
          const response = await baseQuery(
            handleBatchRequest({
              url: `/api/${organizationId}/modification/${entityType}/${entityId}`,
              params: {
                revisionId,
                modificationType,
                schemaType,
              },
              nextBatchCursor,
              limit,
              headers: {
                'X-Api-Version': String(apiVersion),
              },
            })
          );
          let modifications: Modification[] = [];
          if (response?.data?.items) {
            const nextModifications = response.data.items;
            modifications = [...prevModifications, ...nextModifications];

            if (
              response.data?.metadata?.nextCursor &&
              nextModifications.length > 0
            ) {
              return await fetchModifications(
                modifications,
                response.data?.metadata?.nextCursor
              );
            }
          }
          return modifications;
        };

        const modifications = await fetchModifications([]);
        return { data: modifications };
      },
    }),

    getModification: builder.query<Modification, GetModificationArgs>({
      query: ({ organizationId, entityType, entityId, modificationId }) => ({
        url: `/api/${organizationId}/modification/${entityType}/${entityId}/${modificationId}`,
        method: 'GET',
      }),
      providesTags: (_, __, { entityType, entityId, modificationId }) => [
        {
          type: 'Modification',
          modificationId,
          entityType,
          entityId,
        },
      ],
    }),

    createModification: builder.mutation<Modification, CreateModificationArgs>({
      query: ({
        organizationId,
        entityType,
        entityId,
        selectors,
        schemaType,
        content,
        isLocked,
        isActive,
        groupId,
      }) => ({
        url: `/api/${organizationId}/modification/${entityType}/${entityId}`,
        method: 'POST',
        body: {
          schemaType,
          selectors,
          content,
          isLocked,
          isActive,
          groupId,
        },
      }),
      invalidatesTags: (_, __, { entityId }) => [
        { type: 'Draft', id: entityId },
      ],
      onQueryStarted: async (
        { organizationId, entityType, entityId },
        { dispatch, queryFulfilled }
      ) => {
        const { data: createdModification } = await queryFulfilled;
        dispatch(
          editorApi.util.updateQueryData(
            'getModifications',
            {
              organizationId,
              entityType,
              entityId,
              apiVersion: 2,
            },
            (modifications) => {
              modifications.push(createdModification);
            }
          )
        );

        dispatch(
          editorApi.util.upsertQueryData(
            'getModification',
            {
              organizationId,
              entityType,
              entityId,
              modificationId: createdModification.id,
            },
            createdModification
          )
        );
      },
    }),

    updateModification: builder.mutation<Modification, UpdateModificationArgs>({
      query: ({
        organizationId,
        entityType,
        entityId,
        modificationId,
        isActive,
        isLocked,
        order,
        selectors,
        content,
      }) => ({
        url: `/api/${organizationId}/modification/${entityType}/${entityId}/${modificationId}`,
        method: 'POST',
        body: {
          isActive,
          isLocked,
          order,
          selectors,
          content,
        },
      }),
      invalidatesTags: (_, __, { entityId }) => [
        { type: 'Draft', id: entityId },
      ],
      onQueryStarted: async (
        { organizationId, entityType, entityId },
        { dispatch, queryFulfilled }
      ) => {
        const { data: updatedModification } = await queryFulfilled;
        dispatch(
          editorApi.util.updateQueryData(
            'getModifications',
            {
              organizationId,
              entityType,
              entityId,
              apiVersion: 2,
            },
            (modifications) => {
              const index = modifications.findIndex(
                ({ id }) => updatedModification.id === id
              );
              if (index === -1) {
                return;
              }
              modifications.splice(index, 1, updatedModification);
            }
          )
        );

        dispatch(
          editorApi.util.updateQueryData(
            'getModification',
            {
              organizationId,
              entityType,
              entityId,
              modificationId: updatedModification.id,
            },
            (modification) => {
              Object.assign(modification, updatedModification);
            }
          )
        );
      },
    }),

    deleteModification: builder.mutation<Modification, DeleteModificationArgs>({
      query: ({ organizationId, entityType, entityId, modificationId }) => ({
        url: `/api/${organizationId}/modification/${entityType}/${entityId}/${modificationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, { entityId }) => [
        { type: 'Draft', id: entityId },
      ],

      onQueryStarted: async (
        { organizationId, entityType, entityId },
        { dispatch, queryFulfilled }
      ) => {
        const { data: deletedModification } = await queryFulfilled;
        dispatch(
          editorApi.util.updateQueryData(
            'getModifications',
            {
              organizationId,
              entityType,
              entityId,
              apiVersion: 2,
            },
            (modifications) => {
              const index = modifications.findIndex(
                ({ id }) => deletedModification.id === id
              );
              if (index === -1) {
                return;
              }
              modifications.splice(index, 1);
            }
          )
        );

        dispatch(
          editorApi.util.updateQueryData(
            'getModification',
            {
              organizationId,
              entityType,
              entityId,
              modificationId: deletedModification.id,
            },
            (modification) => {
              Object.assign(modification, deletedModification);
            }
          )
        );
      },
    }),

    reorderModification: builder.mutation<
      Modification,
      ReorderModificationsArgs
    >({
      query: ({
        organizationId,
        entityType = 'draft',
        entityId,
        id,
        afterId,
      }) => ({
        url: `/api/${organizationId}/modification/${entityType}/${entityId}/reorder`,
        method: 'POST',
        body: { id, afterId },
      }),
      invalidatesTags: (_, __, { entityId }) => [
        { type: 'Draft', id: entityId },
      ],
      onQueryStarted: async (
        { organizationId, entityType, entityId, id, afterId },
        { dispatch, queryFulfilled }
      ) => {
        const patch = dispatch(
          editorApi.util.updateQueryData(
            'getModifications',
            {
              organizationId,
              entityType,
              entityId,
              apiVersion: 2,
            },
            (modifications) => {
              // Find the index of the modification to be moved
              const modificationIndex = modifications.findIndex(
                ({ id: modificationId }) => id === modificationId
              );

              // If the modification isn't found, exit early
              if (modificationIndex === -1) {
                return;
              }

              // Determine if the modification is a group modification
              const isGroupModification =
                modifications[modificationIndex].modificationType === 'group';

              // Extract the modification to be moved
              const modification = isGroupModification
                ? modifications[modificationIndex]
                : modifications.splice(modificationIndex, 1)[0];

              // Helper function to find the insertion index based on `afterId`
              const findInsertionIndex = (afterId?: string): number => {
                let insertionIndex = -1;

                for (let index = 0; index < modifications.length; index++) {
                  const currentMod = modifications[index];

                  if (currentMod.id === afterId) {
                    insertionIndex = index;
                  }

                  if (
                    insertionIndex !== -1 &&
                    isGroupModification &&
                    index > insertionIndex
                  ) {
                    if (
                      currentMod.groupId !==
                      (modification.content as IAIPromptGroup).id
                    ) {
                      insertionIndex = index;
                      break; // Exit loop early if the correct index is found
                    }
                  }
                }

                return insertionIndex;
              };

              const afterIndex = findInsertionIndex(afterId);

              if (isGroupModification) {
                // Handle reordering for group modifications
                // the logic of group is moving the entire group instead of individual modification
                const groupId = (modification.content as IAIPromptGroup).id;
                const groupCount = modifications.filter(
                  (mod) => mod.groupId === groupId || mod.id === id
                ).length;

                // Extract the group modifications to be moved
                const elementsToMove = modifications.splice(
                  modificationIndex,
                  groupCount
                );

                if (afterIndex === -1) {
                  // Insert elements at the beginning if afterIndex is -1
                  modifications.unshift(...elementsToMove);
                } else {
                  // Insert elements after the found index
                  modifications.splice(afterIndex, 0, ...elementsToMove);
                }
              } else {
                // Handle reordering for non-group modifications
                if (afterIndex === -1) {
                  // Insert modification at the beginning if afterIndex is -1
                  modifications.unshift(modification);
                } else {
                  // Insert modification after the found index
                  modifications.splice(afterIndex + 1, 0, modification);
                }
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch (e) {
          patch.undo();
          throw e;
        }
      },
    }),
    getModificationsCount: builder.query<
      GetModificationsCountResponse,
      GetModificationsCountArgs
    >({
      query: ({ organizationId, entityType = 'demo', entityId }) => ({
        url: `/api/${organizationId}/modification/${entityType}/${entityId}/counts`,
      }),
      providesTags: (_, __, { entityId }) => [
        { type: 'ModificationsCount', id: entityId },
      ],
    }),
    importModifications: builder.mutation<
      ImportModificationsResponse,
      ImportModificationsArgs
    >({
      query: ({
        organizationId,
        entityType = 'demo',
        draftId,
        entityId,
        schemaType,
      }) => ({
        url: `/api/${organizationId}/modification/draft/${draftId}/copy`,
        method: 'POST',
        body: {
          copyFrom: [
            removeUndefinedValues({
              entityType,
              entityId,
              schemaType,
            }),
          ],
        },
      }),
      invalidatesTags: (_, __, { entityId, draftId }) => [
        { type: 'Draft', id: draftId },
        { type: 'Demo', id: entityId },
      ],
    }),
    createSpreadsheet: builder.mutation<
      CreateSpreadsheetResponse,
      CreateSpreadsheetArgs
    >({
      query: ({ organizationId, title, values, draftId }) => ({
        url: `/api/${organizationId}/spreadsheet/draft/${draftId}/new`,
        method: 'POST',
        body: {
          sheets: [
            {
              values,
              freezeHeadersRow: true,
            },
          ],
        },
      }),
    }),
    getSpreadsheetValues: builder.query<
      GetSpreadsheetValuesResponse,
      GetSpreadsheetValuesArgs
    >({
      query: ({ organizationId, spreadsheetId }) => ({
        url: `/api/${organizationId}/spreadsheet/${spreadsheetId}/values`,
      }),
    }),
    editWithAI: builder.mutation<EditWithAIResponse, EditWithAIArgs>({
      query: ({ columnData, columnName, prompt, userData }) => ({
        url: ` "https://data-rule-code-builder-v3jfug5qxq-uc.a.run.app/replace_column"`,
        method: 'POST',
        body: {
          columnData,
          columnName,
          prompt,
          userData,
        },
      }),
    }),
  }),
});

export const {
  // Drafts
  useSaveDraftMutation,
  useDeleteDraftMutation,
  useGetDraftQuery,
  useLazyGetDraftQuery,
  useGetDraftsQuery,
  useLazyGetDraftsQuery,
  // Recorder
  useRecorderSessionRefreshMutation,
  // Charts
  useCreateChartTemplateMutation,
  useGetAllChartTemplatesQuery,
  useLazyGetAllChartTemplatesQuery,
  useDeleteChartTemplateMutation,
  useUpdateChartTemplateMutation,
  // Modifications
  useGetModificationsQuery,
  useLazyGetModificationsQuery,
  useGetModificationQuery,
  useCreateModificationMutation,
  useUpdateModificationMutation,
  useDeleteModificationMutation,
  useReorderModificationMutation,
  useLazyGetModificationsCountQuery,
  useImportModificationsMutation,
  //Google sheets
  useCreateSpreadsheetMutation,
  useLazyGetSpreadsheetValuesQuery,
  useEditWithAIMutation,
} = editorApi;
